/**
 * outboundAnswers — WEB-2123, какие ответы плеер вправе отдать точке ухода.
 *
 * ЗАЧЕМ. В исходящий адрес можно дописать выбранный вариант ответа
 * (`{answer.<идентификатор вопроса>}`), но только у тех вопросов, где автор
 * ЯВНО поставил галочку «передавать ответ в исходящий адрес». Всё остальное,
 * что лежит в форме плеера, — почта, телефон, имя, свободный текст — наружу не
 * уезжает никогда (п.6 требований CTO): адрес виден в журналах чужого сервера
 * и в истории браузера, а наши ниши это похудение и душевное здоровье.
 *
 * ДВА КОНЦА ОДНОГО ПРАВИЛА (CLAUDE.md, WEB-1844). Список типов, у которых
 * ответ — это ИДЕНТИФИКАТОР ВАРИАНТА, а не текст человека, живёт в трёх местах
 * и обязан совпадать:
 *   · схема записи — `backend/src/ai-funnels/block-schema/outbound-answer-optin.ts`
 *     (`OUTBOUND_ANSWER_BLOCK_TYPES`);
 *   · чтение при уходе на сервере — `backend/src/quizzes/utils/exit-target.util.ts`
 *     (`collectOutboundAnswerQuestionIds`);
 *   · сборка в плеере — этот файл.
 * Сервер — последний арбитр: он ещё раз фильтрует приехавшее по опубликованному
 * содержимому, поэтому расхождение здесь может только НЕДОДАТЬ, но не протащить
 * запрещённое. Совпадение множеств первых двух концов стережёт
 * `outbound-answer-optin.web2123.spec.ts`.
 *
 * ВТОРОЕ ДВУСТОРОННЕЕ ПРАВИЛО ЭТОГО ФАЙЛА (свежий взгляд пачки 46, находка 2):
 * запрет ответов в ЧУВСТВИТЕЛЬНОЙ НИШЕ. Сервер считает его у точки ухода
 * (`exit-target.util.ts`) и отдаёт готовым признаком в публичном ответе квиза;
 * здесь он гасит сборку целиком — иначе ветка ухода через блок-кнопку
 * (WEB-2164 п.6, сервер её не видит) осталась бы без запрета. Сторож обоих
 * концов — `backend/src/shared/guards/outbound-answers-niche-ends.fv46.spec.ts`.
 */

import { ANSWER_MACRO_PREFIX } from '@/utils/outboundUrlMacros';

/** Типы, у которых ответ — идентификатор варианта. Копия серверного набора. */
export const OUTBOUND_ANSWER_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'single_choice',
  'multiple_choice',
  'multi_step_question',
]);

/**
 * Готовая метка ответа вопроса для исходящего адреса: `{answer.<id вопроса>}`.
 *
 * WEB-2123, остаток по решению владельца 2123-Р1: панель свойств показывает её
 * автору под включённой галочкой, чтобы метку не приходилось писать вслепую.
 * Префикс — из той же константы, что читает подстановка кабинета
 * (`utils/outboundUrlMacros.ts`); совпадение с серверной стережёт
 * `outbound-macros-two-players-ends.web2262.spec.ts`. Своей строки `answer.`
 * здесь нет намеренно — копия разъехалась бы молча (WEB-1844).
 */
export function answerMacroToken(questionId: string): string {
  return `{${ANSWER_MACRO_PREFIX}${questionId}}`;
}

/** Потолок обхода — то же, что на сервере: чужое содержимое не бесконечно. */
const MAX_NODES = 100_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Идентификаторы вопросов, у которых автор включил передачу ответа.
 *
 * Обход ГЛУБОКИЙ, а не по `screen.blocks`: вопрос может лежать внутри
 * блока-контейнера, и плоский перебор молча потерял бы включённую галочку —
 * автор увидел бы пустой параметр и не понял почему.
 */
export function collectOutboundAnswerBlockIds(content: unknown): Set<string> {
  const allowed = new Set<string>();
  let seen = 0;
  const stack: unknown[] = [content];
  while (stack.length > 0) {
    if (++seen > MAX_NODES) return allowed;
    const node = stack.pop();
    if (Array.isArray(node)) {
      for (const child of node) stack.push(child);
      continue;
    }
    if (!isRecord(node)) continue;
    if (
      typeof node.type === 'string' &&
      OUTBOUND_ANSWER_BLOCK_TYPES.has(node.type) &&
      node.passAnswerToOutboundUrl === true &&
      typeof node.id === 'string' &&
      node.id.length > 0
    ) {
      allowed.add(node.id);
    }
    for (const value of Object.values(node)) {
      if (typeof value === 'object' && value !== null) stack.push(value);
    }
  }
  return allowed;
}

/**
 * Пары «вопрос → ответ(ы)» для точки ухода: только включённые автором вопросы
 * и только те, на которые человек ответил.
 */
export function collectOutboundAnswers(
  content: unknown,
  values: Record<string, string>,
  /**
   * СВЕЖИЙ ВЗГЛЯД ПАЧКИ 46 (находка 2) — «ответы наружу запрещены».
   *
   * Признак приезжает ГОТОВЫМ от сервера (`outboundAnswersBlocked` в публичном
   * ответе квиза): в чувствительной нише идентификатор варианта и есть диагноз,
   * и отдавать его нельзя ни по какой галочке автора (WEB-2159). Перечень ниш в
   * браузер не копируется — вторая копия разъехалась бы молча (шрам WEB-953,
   * WEB-1687); сервер считает его правилом `isSensitiveNicheKey` один раз.
   *
   * Гейт стоит ЗДЕСЬ, а не у одной из веток ухода, потому что веток две:
   * серверная точка ухода (там запрет уже был) и блок-кнопка со ссылкой,
   * которая через сервер не идёт намеренно (WEB-2164 п.6). Правило одно на обе.
   *
   * Отсутствие признака читается как «не запрещено»: старый закешированный
   * ответ поля не несёт, и поведение таких воронок не меняется.
   */
  outboundAnswersBlocked?: boolean,
): Record<string, string> {
  if (outboundAnswersBlocked === true) return {};
  const allowed = collectOutboundAnswerBlockIds(content);
  if (allowed.size === 0) return {};
  const out: Record<string, string> = {};
  for (const blockId of allowed) {
    const raw = values[blockId];
    if (typeof raw !== 'string' || raw === '') continue;
    out[blockId] = raw;
  }
  return out;
}
